import { useCallback, useEffect, useRef, useState } from "react";

import { useDebouncedValue } from "./useDebouncedSearchQuery";
import {
  checkPasswordPolicy,
  isPasswordPolicyResult,
  type PasswordPolicyAudience,
  type PasswordPolicyResult,
} from "../queries/auth/passwordPolicyClient";

type UsePasswordPolicyValidationOptions = {
  email?: string;
  password: string;
  audience: PasswordPolicyAudience;
  enabled?: boolean;
  delayMs?: number;
};

export const usePasswordPolicyValidation = ({
  email,
  password,
  audience,
  enabled = true,
  delayMs = 300,
}: UsePasswordPolicyValidationOptions) => {
  const normalizedEmail = email?.trim() ?? "";
  const hasPassword = password.length > 0;
  const debouncedPassword = useDebouncedValue(password, delayMs);
  const currentInputRef = useRef({ email: normalizedEmail, password, audience });
  const inputVersionRef = useRef(0);
  const requestVersionRef = useRef(0);
  const [validatedInputVersion, setValidatedInputVersion] = useState<
    number | null
  >(null);
  const [policyResult, setPolicyResult] =
    useState<PasswordPolicyResult | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [errorInputVersion, setErrorInputVersion] = useState<number | null>(
    null,
  );

  const previousInput = currentInputRef.current;
  if (
    previousInput.email !== normalizedEmail ||
    previousInput.password !== password ||
    previousInput.audience !== audience
  ) {
    currentInputRef.current = {
      email: normalizedEmail,
      password,
      audience,
    };
    inputVersionRef.current += 1;
  }

  useEffect(() => {
    if (
      !enabled ||
      !normalizedEmail ||
      !hasPassword ||
      !debouncedPassword
    ) {
      requestVersionRef.current += 1;
      setValidatedInputVersion(null);
      setPolicyResult(null);
      setIsRequesting(false);
      setErrorInputVersion(null);
      return undefined;
    }

    const controller = new AbortController();
    const requestVersion = requestVersionRef.current + 1;
    const inputVersion = inputVersionRef.current;
    requestVersionRef.current = requestVersion;
    setIsRequesting(true);
    setErrorInputVersion(null);

    checkPasswordPolicy(
      audience,
      { email: normalizedEmail, new_password: debouncedPassword },
      controller.signal,
    )
      .then((result) => {
        if (
          requestVersionRef.current !== requestVersion ||
          currentInputRef.current.email !== normalizedEmail ||
          currentInputRef.current.password !== debouncedPassword ||
          currentInputRef.current.audience !== audience
        ) {
          return;
        }
        if (!isPasswordPolicyResult(result)) {
          throw new Error("Malformed password policy response");
        }
        setPolicyResult(result);
        setValidatedInputVersion(inputVersion);
      })
      .catch(() => {
        if (
          controller.signal.aborted ||
          requestVersionRef.current !== requestVersion
        ) {
          return;
        }
        setValidatedInputVersion(null);
        setErrorInputVersion(inputVersion);
      })
      .finally(() => {
        if (requestVersionRef.current === requestVersion) {
          setIsRequesting(false);
        }
      });

    return () => controller.abort();
  }, [audience, debouncedPassword, enabled, hasPassword, normalizedEmail]);

  const applyPolicyResult = useCallback((result: unknown) => {
    if (!isPasswordPolicyResult(result)) return false;
    requestVersionRef.current += 1;
    setPolicyResult(result);
    setValidatedInputVersion(inputVersionRef.current);
    setIsRequesting(false);
    setErrorInputVersion(null);
    return true;
  }, []);

  const isCurrent =
    enabled &&
    Boolean(normalizedEmail) &&
    hasPassword &&
    validatedInputVersion === inputVersionRef.current &&
    debouncedPassword === password &&
    !isRequesting;
  const hasError = errorInputVersion === inputVersionRef.current;

  return {
    policyResult,
    isCurrent,
    isPending:
      enabled &&
      Boolean(normalizedEmail) &&
      hasPassword &&
      !isCurrent &&
      !hasError,
    hasError,
    allPassed: Boolean(isCurrent && policyResult?.all_passed),
    applyPolicyResult,
  };
};
